import React, { useState } from 'react';
import { Button, Card, Typography, Alert, Space, Input, Form, theme } from 'antd';
import { LoginOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import PasswordChangeModal from './PasswordChangeModal';

const { Title, Text } = Typography;

interface LoginFormProps {
  onSuccess?: () => void;
}

interface LoginValues {
  username: string;
  password: string;
}

const LoginForm: React.FC<LoginFormProps> = () => {
  const { token } = theme.useToken();
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm<LoginValues>();
  const { loginWithAzureAD, error, clearError, passwordResetState } = useAuthStore();
  
  // Debug: Log error state when it changes
  React.useEffect(() => {
    if (error) {
      console.log('LoginForm: Error state updated:', {
        code: error.code,
        message: error.message,
        details: error.details,
      });
    }
  }, [error]);

  const handleAzureADLogin = async (values: LoginValues) => {
    try {
      setIsLoading(true);
      clearError();
      
      // Call loginWithAzureAD - this will redirect to Azure AD with prompt=login
      // The username will be pre-filled, but Azure AD will prompt for password
      await loginWithAzureAD(values.username, values.password);
      
      // Note: loginWithAzureAD redirects, so we won't reach here normally
      // Don't set loading to false - the redirect will change the page
    } catch (err: any) {
      console.error('Azure AD login error:', err);
      setIsLoading(false);
      // Error is already set in the store by loginWithAzureAD
    }
  };

  return (
    <>
      <PasswordChangeModal 
        visible={passwordResetState?.isRequired || false}
        username={passwordResetState?.username || ''}
      />
      
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <Card 
        style={{ 
          width: '100%', 
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: '12px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0, color: token.colorPrimary }}>
            Data Exchange
          </Title>
          <Text type="secondary">
            Secure File Transfer Platform
          </Text>
        </div>

        {error && (
          <Alert
            message={error.code === 'SERVICE_ACCOUNT_NOT_ALLOWED' ? 'Service Account Not Allowed' : error.code === 'SESSION_EXPIRED' ? 'Session expired' : 'Login Failed'}
            description={error.message}
            type="error"
            showIcon
            style={{ 
              marginBottom: '24px',
              ...(error.code === 'SERVICE_ACCOUNT_NOT_ALLOWED' ? {
                border: '2px solid #ff4d4f',
                backgroundColor: '#fff2f0'
              } : {})
            }}
            closable
            onClose={clearError}
          />
        )}

        <Form
          form={form}
          name="azure-ad-login"
          onFinish={handleAzureADLogin}
          layout="vertical"
          style={{ marginTop: '24px' }}
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: 'Please enter your Azure AD username (email)' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Azure AD Email"
              size="large"
              disabled={isLoading}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please enter your password' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
              disabled={isLoading}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              icon={<LoginOutlined />}
              block
              size="large"
              style={{ 
                height: '48px',
                fontSize: '16px'
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In with Azure AD'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Space direction="vertical" size="small">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Secure file transfer powered by AWS
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Contact your administrator for access
            </Text>
          </Space>
        </div>
      </Card>
    </div>
    </>
  );
};

export default LoginForm;
